import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardHeader,
  Row,
  Col,
  Container,
  Badge,
} from 'reactstrap';
import AsyncSelect from 'react-select/async';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import BreadCrumb from '../../../Components/Common/BreadCrumb';
import Can from '../../../Components/Common/Can';
import ActionButton from '../../../Components/Common/ActionButton';
import { DetailBackButton } from '../../../Components/Common/Detail';
import { getCrude, getCrudeList } from './components/registrationApi';
import AssayEditorSection from './components/AssayEditorSection';
import AssayDetailTabs from './components/AssayDetailTabs';

// Crude Registration — Detail page (rule-crude-registration.md §Detail). Read-only
// crude header + a searchable Crude selector (§Detail.1) + an Assay table
// (§Detail.8). Each assay row is an EXPANDABLE row (matching `asay detail.png`):
//   • chevron  → expands the row READ-ONLY (AssayDetailTabs — 3 tabs, view only)
//   • pencil   → expands the SAME row in EDIT mode (AssayEditorSection, editable)
// Add-Assay (§Detail.9) stays a separate below-table editor card, unchanged.
//
// Expansion is controlled (expandableRowExpanded + onRowExpandToggled) so the
// pencil can force a row open in edit mode; `editingId` marks which expanded row
// is editable vs read-only.
//
// GetCrude/{id} returns a 500 (not 404) for a missing id (api §3) — any rejection
// is treated as "not found".
const RegistrationDetailView = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  const [crude, setCrude] = useState(null);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);

  // Add-Assay editor (below the table): 'view' (closed) or 'add'. Edit is NOT a
  // mode here — it happens inline via the expandable row (editingId below).
  const [mode, setMode] = useState('view');

  // Controlled expandable-row state for the Assay table.
  //   expandedIds — set of row ids currently expanded (chevron or pencil).
  //   editingId   — the one expanded row (if any) shown in EDIT mode; all other
  //                 expanded rows are read-only.
  const [expandedIds, setExpandedIds] = useState(() => new Set());
  const [editingId, setEditingId] = useState(null);

  document.title = 'Registration Detail';

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    setNotFound(false);
    setMode('view');
    setExpandedIds(new Set());
    setEditingId(null);
    try {
      const result = await getCrude(id);
      if (!result) {
        setNotFound(true);
        setCrude(null);
      } else {
        setCrude(result);
      }
    } catch (error) {
      setNotFound(true);
      setCrude(null);
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => {
    fetchDetail();
  }, [fetchDetail]);

  // §Detail.1 — Crude selector: search by Crude Name, options show "G5 —
  // Crude Name". Selecting navigates to that crude's detail.
  const loadCrudeOptions = async inputValue => {
    try {
      const items = await getCrudeList({ searchText: inputValue });
      return items.map(c => ({
        value: c.id,
        label: `${c.codeG5 || '-'} — ${c.crudeName}`,
      }));
    } catch (error) {
      toast.error(`Failed to search crude: ${error}`);
      return [];
    }
  };

  const handleSelectCrude = option => {
    if (option?.value && option.value !== id) {
      navigate(`/crude/registration/dtl/${option.value}`);
    }
  };

  // §Detail.10 — Add/Edit is locked while the crude is awaiting review
  // (dataStatus !== 'Active'); the Blazor UI greys the actions out.
  const isLocked = crude?.dataStatus && crude.dataStatus !== 'Active';

  const openAdd = () => setMode('add');
  const closeAdd = () => setMode('view');

  // Chevron toggle — accordion behaviour: only ONE row may be open at a time, so
  // expanding a row collapses any other open row. Toggling the same row closes it.
  // Collapsing a row that was in edit mode also drops its edit flag.
  const toggleExpanded = rowId => {
    setExpandedIds(prev => {
      if (prev.has(rowId)) {
        // Toggle-off the same row → nothing open.
        if (editingId === rowId) setEditingId(null);
        return new Set();
      }
      // Open a new row as the only one open → any other row (including one being
      // edited) collapses, so clear the edit flag too.
      if (editingId !== null) setEditingId(null);
      return new Set([rowId]);
    });
  };

  // Pencil — accordion: make this the only open row AND mark it editable, so Edit
  // visually expands the row in edit mode (per the reference screenshot) while
  // collapsing any previously-open row.
  const openEdit = rowId => {
    setExpandedIds(new Set([rowId]));
    setEditingId(rowId);
  };

  // Close/cancel from the inline edit editor → back to read-only (stay expanded).
  const closeEdit = () => setEditingId(null);

  const handleSaved = () => {
    // AddDetail/UpdateDetail don't take effect until approved; refetch so the
    // header/lock state reflects the new OnProcess status (also resets expansion).
    fetchDetail();
  };

  // Expanded row content. Edit mode renders the editable AssayEditorSection
  // inline; otherwise the read-only AssayDetailTabs.
  const renderExpanded = data =>
    editingId === data.id && !isLocked ? (
      <div className='p-2'>
        <AssayEditorSection
          mode='edit'
          crude={crude}
          initialDetail={data}
          onClose={closeEdit}
          onSaved={handleSaved}
        />
      </div>
    ) : (
      <AssayDetailTabs crude={crude} detail={data} />
    );

  if (loading) {
    return (
      <div className='page-content'>
        <Container fluid>
          <div className='text-center py-5'>Loading...</div>
        </Container>
      </div>
    );
  }

  if (notFound || !crude) {
    return (
      <React.Fragment>
        <div className='page-content'>
          <Container fluid>
            <BreadCrumb title='Not Found' pageTitle='CRUDE' />
            <Row>
              <Col lg={12}>
                <Card>
                  <CardBody className='text-center py-5'>
                    <h5>Data tidak ditemukan</h5>
                    <Link
                      to='/crude/registration'
                      className='btn btn-primary btn-sm mt-3'
                    >
                      <i className='ri-arrow-left-line align-middle me-2'></i>
                      Back to List
                    </Link>
                  </CardBody>
                </Card>
              </Col>
            </Row>
          </Container>
        </div>
      </React.Fragment>
    );
  }

  const headerFields = [
    [
      { label: 'Crude Name', value: crude.crudeName },
      { label: 'Origin', value: crude.origin },
    ],
    [
      { label: 'Country', value: crude.country },
      { label: 'Code G5', value: crude.codeG5 },
    ],
    [
      { label: 'Production Rate (MBCD)', value: crude.productionRate },
      { label: 'Parcel Size (MB)', value: crude.parcelSize },
    ],
  ];

  return (
    <React.Fragment>
      <div className='page-content'>
        <Container fluid>
          <BreadCrumb title='Registration Detail' pageTitle='CRUDE' />

          <div className='dt-header__top'>
            <DetailBackButton
              label='Back to list'
              onClick={() => navigate('/crude/registration')}
            />
            {isLocked && (
              <Badge color='warning'>
                {crude.dataStatus} — awaiting review
              </Badge>
            )}
          </div>

          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className='d-flex align-items-center flex-wrap gap-2'>
                  <h5 className='card-title mb-0 flex-grow-1'>Crude Detail</h5>
                </CardHeader>
                <CardBody>
                  {/* §Detail.1 — searchable Crude selector */}
                  <Row className='mb-4'>
                    <Col lg={5} md={8}>
                      <label className='form-label'>Search Crude</label>
                      <AsyncSelect
                        cacheOptions
                        defaultOptions
                        isClearable
                        placeholder='Search by Crude Name...'
                        loadOptions={loadCrudeOptions}
                        onChange={handleSelectCrude}
                      />
                    </Col>
                  </Row>

                  {/* §Detail.2–7 — read-only header fields */}
                  {headerFields.map((pair, i) => (
                    <Row key={i} className='border-bottom py-2'>
                      {pair.map((field, j) => (
                        <Col md={6} key={j}>
                          <div className='text-muted text-uppercase fs-12 fw-semibold'>
                            {field.label}
                          </div>
                          <div>{field.value || '-'}</div>
                        </Col>
                      ))}
                    </Row>
                  ))}
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* §Detail.8 — Assay table */}
          <Row>
            <Col lg={12}>
              <Card>
                <CardHeader className='d-flex align-items-center flex-wrap gap-2'>
                  <h5 className='card-title mb-0 flex-grow-1'>Assay</h5>
                  <Can action='canCreate'>
                    <ActionButton
                      variant='primary'
                      size='sm'
                      icon='ri-add-line'
                      title={
                        isLocked ? 'Locked — awaiting review' : 'Add Assay'
                      }
                      disabled={!!isLocked}
                      onClick={openAdd}
                    >
                      Add Assay
                    </ActionButton>
                  </Can>
                </CardHeader>
                <CardBody className='pt-0'>
                  {/* Hand-built table so the header can group Sulfur/Yield
                      Classification under one "Classification" banner via
                      colSpan/rowSpan (matches asay detail.png) — react-data-table
                      -component has no grouped-header support. Each data row is
                      followed by a full-width expanded row (chevron = read-only,
                      pencil = edit), replicating the RDT expandable behaviour. */}
                  <div className='table-responsive'>
                    <table className='table table-bordered align-middle mb-0'>
                      <thead className='table-primary'>
                        <tr>
                          <th rowSpan={2} style={{ width: '40px' }}></th>
                          <th rowSpan={2}>Assay Year</th>
                          <th colSpan={2} className='text-center'>
                            Classification
                          </th>
                          <th rowSpan={2}>Lab</th>
                          <th rowSpan={2}>Sulfur</th>
                          <th rowSpan={2}>Tan</th>
                          <th rowSpan={2} className='text-center'>
                            Last Update ?
                          </th>
                          <th
                            rowSpan={2}
                            className='text-center'
                            style={{ width: '90px' }}
                          >
                            Action
                          </th>
                        </tr>
                        <tr>
                          <th>Sulfur Classification</th>
                          <th>Yield Classification</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(crude.details || []).length === 0 && (
                          <tr>
                            <td
                              colSpan={9}
                              className='text-center text-muted py-3'
                            >
                              No assay data
                            </td>
                          </tr>
                        )}
                        {(crude.details || []).map(row => {
                          const expanded = expandedIds.has(row.id);
                          return (
                            <React.Fragment key={row.id}>
                              <tr>
                                <td className='text-center'>
                                  <ActionButton
                                    variant='icon'
                                    size='sm'
                                    icon={`ri-arrow-${
                                      expanded ? 'up' : 'down'
                                    }-s-line`}
                                    label={expanded ? 'Collapse' : 'Expand'}
                                    onClick={() => toggleExpanded(row.id)}
                                  />
                                </td>
                                <td>{row.assayYear}</td>
                                <td>{row.classificationSulfur || '-'}</td>
                                <td>{row.classificationYield || '-'}</td>
                                <td>{row.lab || '-'}</td>
                                <td>{row.sulfur ?? '-'}</td>
                                <td>{row.tan ?? '-'}</td>
                                <td className='text-center'>
                                  {row.isLastUpdate ? (
                                    <Badge color='success'>Yes</Badge>
                                  ) : (
                                    <Badge color='light' className='text-muted'>
                                      No
                                    </Badge>
                                  )}
                                </td>
                                <td className='text-center'>
                                  <Can action='canUpdate'>
                                    <ActionButton
                                      variant='icon'
                                      size='sm'
                                      icon='ri-edit-line'
                                      label={
                                        isLocked
                                          ? 'Locked — awaiting review'
                                          : 'Edit'
                                      }
                                      disabled={!!isLocked}
                                      onClick={() => openEdit(row.id)}
                                    />
                                  </Can>
                                </td>
                              </tr>
                              {expanded && (
                                <tr>
                                  <td colSpan={9} className='p-0 bg-white'>
                                    {renderExpanded(row)}
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                </CardBody>
              </Card>
            </Col>
          </Row>

          {/* §Detail.9 — Add Assay editor (separate below-table card; Edit is
              handled inline in the expandable row above). */}
          {mode === 'add' && (
            <Row>
              <Col lg={12}>
                <AssayEditorSection
                  mode='add'
                  crude={crude}
                  initialDetail={null}
                  onClose={closeAdd}
                  onSaved={handleSaved}
                />
              </Col>
            </Row>
          )}

          <ToastContainer closeButton={false} limit={1} />
        </Container>
      </div>
    </React.Fragment>
  );
};

export default RegistrationDetailView;
